import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';

// ─── Camera Lens (eye) ───────────────────────────────────────────────────────
function CameraLens({ wrapRef, pupilRef, size = 200, glowDelay = 0 }) {
  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
    >
      {/* Ambient glow behind lens */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: -20,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,42,31,0.10) 0%, transparent 60%)',
          animation: `rec-pulse ${3 + glowDelay * 0.5}s ease-in-out infinite`,
          animationDelay: `${glowDelay}s`,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Lens SVG — static rings, aperture marks, iris */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* Outer body */}
        <circle cx="100" cy="100" r="98" fill="#070707" stroke="rgba(242,239,232,0.14)" strokeWidth="1.5" />
        {/* Barrel ring */}
        <circle cx="100" cy="100" r="86" fill="#040404" stroke="rgba(242,239,232,0.06)" strokeWidth="0.75" />
        {/* Aperture marks — 24 ticks */}
        {Array.from({ length: 24 }).map((_, i) => {
          const ang  = (i / 24) * Math.PI * 2 - Math.PI / 2;
          const main = i % 6 === 0;
          const r1   = 83;
          const r2   = main ? 72 : 78;
          return (
            <line
              key={i}
              x1={100 + r1 * Math.cos(ang)}
              y1={100 + r1 * Math.sin(ang)}
              x2={100 + r2 * Math.cos(ang)}
              y2={100 + r2 * Math.sin(ang)}
              stroke={`rgba(242,239,232,${main ? 0.28 : 0.10})`}
              strokeWidth={main ? 1 : 0.5}
            />
          );
        })}
        {/* Measurement scale labels at 3/6/9/12 o'clock */}
        {[{ a: -Math.PI / 2, t: '∞' }, { a: 0, t: '2' }, { a: Math.PI / 2, t: '1.2' }, { a: Math.PI, t: '0.7' }].map(({ a, t }, i) => (
          <text
            key={i}
            x={100 + 94 * Math.cos(a)}
            y={100 + 94 * Math.sin(a) + 3}
            textAnchor="middle"
            fill="rgba(242,239,232,0.18)"
            fontSize="6"
            fontFamily="monospace"
          >{t}</text>
        ))}
        {/* Iris — outer */}
        <circle cx="100" cy="100" r="64" fill="#0c0c0c" stroke="rgba(255,42,31,0.22)" strokeWidth="0.75" />
        {/* Iris — aperture layers */}
        <circle cx="100" cy="100" r="56" fill="#090909" stroke="rgba(242,239,232,0.06)" strokeWidth="0.5" />
        <circle cx="100" cy="100" r="48" fill="#070707" stroke="rgba(242,239,232,0.04)" strokeWidth="0.5" />
        {/* Focus ring */}
        <circle cx="100" cy="100" r="40" fill="#060606" stroke="rgba(242,239,232,0.14)" strokeWidth="0.75" />
      </svg>

      {/* Moving pupil */}
      <div
        ref={pupilRef}
        style={{
          position: 'absolute',
          top: '50%', left: '50%',
          width: 54, height: 54,
          borderRadius: '50%',
          background: 'radial-gradient(circle, #010101 55%, #080808 100%)',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      />

      {/* Catch light — primary */}
      <div aria-hidden="true" style={{
        position: 'absolute',
        width: 13, height: 13,
        borderRadius: '50%',
        background: 'rgba(242,239,232,0.22)',
        top: '30%', left: '58%',
        transform: 'translate(-50%, -50%)',
        zIndex: 3,
        filter: 'blur(2px)',
        pointerEvents: 'none',
      }} />
      {/* Catch light — secondary */}
      <div aria-hidden="true" style={{
        position: 'absolute',
        width: 5, height: 5,
        borderRadius: '50%',
        background: 'rgba(242,239,232,0.10)',
        top: '38%', left: '52%',
        transform: 'translate(-50%, -50%)',
        zIndex: 3,
        pointerEvents: 'none',
      }} />
    </div>
  );
}

// ─── 404 Page ─────────────────────────────────────────────────────────────────
export default function NotFound() {
  const eye1Wrap  = useRef(null);
  const eye2Wrap  = useRef(null);
  const pupil1    = useRef(null);
  const pupil2    = useRef(null);
  const labelRef  = useRef(null);
  const eyeRow    = useRef(null);
  const numRef    = useRef(null);
  const subRef    = useRef(null);
  const descRef   = useRef(null);
  const ctaRef    = useRef(null);
  const footerRef = useRef(null);

  const LENS_SIZE   = 200;
  const MAX_OFFSET  = 22; // px pupil can travel from center

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ── Mount animation ────────────────────────────────────────────────────── */
    if (!reduced) {
      gsap.set([eye1Wrap.current, eye2Wrap.current], { opacity: 0, scale: 0.38, y: 16 });
      gsap.set(numRef.current,  { opacity: 0, y: 70, skewY: 6 });
      gsap.set(subRef.current,  { opacity: 0, y: 30 });
      gsap.set(descRef.current, { opacity: 0, y: 20 });
      gsap.set(ctaRef.current,  { opacity: 0, y: 16, scale: 0.94 });
      gsap.set(labelRef.current,  { opacity: 0 });
      gsap.set(footerRef.current, { opacity: 0 });

      gsap.timeline({ defaults: { ease: 'expo.out' } })
        .to(eye1Wrap.current, { opacity: 1, scale: 1, y: 0, duration: 0.95, ease: 'back.out(1.4)' }, 0.08)
        .to(eye2Wrap.current, { opacity: 1, scale: 1, y: 0, duration: 0.95, ease: 'back.out(1.4)' }, 0.16)
        .to(numRef.current,   { opacity: 1, y: 0, skewY: 0, duration: 0.78, ease: 'power3.out' }, 0.30)
        .to(subRef.current,   { opacity: 1, y: 0, duration: 0.60 }, 0.48)
        .to(labelRef.current,   { opacity: 1, duration: 0.45 }, 0.48)
        .to(descRef.current,  { opacity: 1, y: 0, duration: 0.50 }, 0.62)
        .to(ctaRef.current,   { opacity: 1, y: 0, scale: 1, duration: 0.60, ease: 'back.out(2)' }, 0.74)
        .to(footerRef.current, { opacity: 1, duration: 0.40 }, 0.82);
    }

    /* ── Eye tracking ───────────────────────────────────────────────────────── */
    if (reduced) return;

    const xTo1 = gsap.quickTo(pupil1.current, 'x', { duration: 0.52, ease: 'power2.out' });
    const yTo1 = gsap.quickTo(pupil1.current, 'y', { duration: 0.52, ease: 'power2.out' });
    const xTo2 = gsap.quickTo(pupil2.current, 'x', { duration: 0.60, ease: 'power2.out' });
    const yTo2 = gsap.quickTo(pupil2.current, 'y', { duration: 0.60, ease: 'power2.out' });

    const onMove = (e) => {
      const track = (wrapRef, xTo, yTo) => {
        const r = wrapRef.current?.getBoundingClientRect();
        if (!r) return;
        const dx   = e.clientX - (r.left + r.width / 2);
        const dy   = e.clientY - (r.top  + r.height / 2);
        const ang  = Math.atan2(dy, dx);
        const dist = Math.min(Math.hypot(dx, dy) * 0.09, MAX_OFFSET);
        xTo(Math.cos(ang) * dist);
        yTo(Math.sin(ang) * dist);
      };
      track(eye1Wrap, xTo1, yTo1);
      track(eye2Wrap, xTo2, yTo2);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: '#030303',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        color: 'var(--bone, #f2efe8)',
      }}
    >
      {/* Grain overlay */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23g)' opacity='0.2'/%3E%3C/svg%3E")`,
          opacity: 0.45, mixBlendMode: 'overlay', zIndex: 1,
        }}
      />
      {/* Vignette */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
          background: 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.72) 100%)',
        }}
      />

      {/* ── Top label ─────────────────────────────────────────────────────── */}
      <div
        ref={labelRef}
        style={{
          position: 'absolute', top: 36, left: 'clamp(24px,4vw,64px)', right: 'clamp(24px,4vw,64px)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontFamily: 'var(--font-mono, "Courier New", monospace)',
          fontSize: 10, letterSpacing: '0.22em',
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', color: 'rgba(242,239,232,0.50)' }}>
          <span style={{ color: '#ff2a1f' }}>[ 404 ]</span>
          <span style={{ width: 36, height: 1, background: 'rgba(242,239,232,0.25)', display: 'inline-block' }} />
          ERROR
        </div>
        <div style={{ display: 'flex', gap: 7, alignItems: 'center', color: 'rgba(255,42,31,0.9)', fontSize: 9 }}>
          <span style={{
            width: 5, height: 5, borderRadius: '50%', display: 'inline-block',
            background: '#ff2a1f', boxShadow: '0 0 8px rgba(255,42,31,0.85)',
            animation: 'rec-pulse 1.4s ease-in-out infinite',
          }} />
          SEARCHING
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: 'relative', zIndex: 5,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(18px, 2.8vh, 32px)',
          padding: '0 clamp(24px,4vw,64px)',
        }}
      >
        {/* Camera eyes */}
        <div
          ref={eyeRow}
          style={{ display: 'flex', gap: 'clamp(20px, 3.5vw, 52px)', alignItems: 'center' }}
        >
          <CameraLens wrapRef={eye1Wrap} pupilRef={pupil1} size={LENS_SIZE} glowDelay={0} />
          <CameraLens wrapRef={eye2Wrap} pupilRef={pupil2} size={LENS_SIZE} glowDelay={0.7} />
        </div>

        {/* 404 numeral */}
        <div
          ref={numRef}
          aria-label="404"
          style={{
            fontFamily: '"Arial Black", "Arial Bold", Impact, sans-serif',
            fontSize: 'clamp(80px, 14vw, 176px)',
            fontWeight: 900,
            letterSpacing: '-0.07em',
            lineHeight: 0.85,
            color: 'rgba(242,239,232,0.95)',
            textAlign: 'center',
            willChange: 'transform, opacity',
            textShadow: '0 0 80px rgba(255,42,31,0.10)',
          }}
        >
          404
        </div>

        {/* Subtitle */}
        <div
          ref={subRef}
          style={{
            fontFamily: 'var(--font-mono, "Courier New", monospace)',
            fontSize: 'clamp(10px, 1.4vw, 15px)',
            letterSpacing: '0.42em',
            textTransform: 'uppercase',
            color: 'rgba(242,239,232,0.38)',
            textAlign: 'center',
            willChange: 'transform, opacity',
          }}
        >
          FRAME NOT FOUND
        </div>

        {/* Description */}
        <div
          ref={descRef}
          style={{
            fontFamily: 'var(--font-mono, "Courier New", monospace)',
            fontSize: 12,
            letterSpacing: '0.14em',
            color: 'rgba(242,239,232,0.26)',
            textAlign: 'center',
            maxWidth: 380,
            lineHeight: 1.8,
            willChange: 'transform, opacity',
          }}
        >
          This shot doesn't exist in the roll.
          <br />We've searched every frame. Nothing here.
        </div>

        {/* CTA */}
        <Link
          ref={ctaRef}
          to="/"
          style={{
            marginTop: 6,
            display: 'inline-flex', alignItems: 'center', gap: 10,
            fontFamily: 'var(--font-mono, "Courier New", monospace)',
            fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
            textDecoration: 'none',
            color: '#ff2a1f',
            border: '1px solid rgba(255,42,31,0.60)',
            padding: '13px 30px',
            borderRadius: 2,
            background: 'rgba(255,42,31,0.08)',
            backdropFilter: 'blur(6px)',
            transition: 'background 200ms ease, border-color 200ms ease, color 200ms ease, box-shadow 200ms ease',
            boxShadow: '0 0 20px rgba(255,42,31,0.14)',
            willChange: 'transform, opacity',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background    = 'rgba(255,42,31,0.22)';
            e.currentTarget.style.borderColor   = 'rgba(255,42,31,1)';
            e.currentTarget.style.color         = '#fff';
            e.currentTarget.style.boxShadow     = '0 0 36px rgba(255,42,31,0.40)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background    = 'rgba(255,42,31,0.08)';
            e.currentTarget.style.borderColor   = 'rgba(255,42,31,0.60)';
            e.currentTarget.style.color         = '#ff2a1f';
            e.currentTarget.style.boxShadow     = '0 0 20px rgba(255,42,31,0.14)';
          }}
        >
          ← BACK TO PORTFOLIO
        </Link>
      </div>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <div
        ref={footerRef}
        style={{
          position: 'absolute',
          bottom: 28,
          left: 'clamp(24px,4vw,64px)', right: 'clamp(24px,4vw,64px)',
          display: 'flex', justifyContent: 'space-between',
          fontFamily: 'var(--font-mono, "Courier New", monospace)',
          fontSize: 9, letterSpacing: '0.22em',
          color: 'rgba(242,239,232,0.20)',
          zIndex: 10,
        }}
      >
        <span>[ 00 / 00 ]</span>
        <span>DREW HALLÉ © 2026</span>
        <span>↑ REWIND</span>
      </div>
    </div>
  );
}
